/* eslint-disable max-lines -- Tech Debt */
import {
  Directive,
  ElementRef,
  inject,
  InjectionToken,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { isEqual, some, times } from '@sama/micro-dash';
import { withHistory } from '@sama/rxjs-core';
import { clamp, forEach, isEmpty } from 'lodash';
import { combineLatest, from, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { MIN_ZOOM_SIZE_MULTIPLIER } from '../../../2d/shared/utils/view-box.service';
import { outputInfoStoreToken } from '../../../core/h3ml/state/output-info';
import { fusionStoreToken } from '../../../core/h3ml/state/task-workspace-store';
import { getOutputAnnotationInfoStore } from '../../../core/h3ml/state/task-workspace-store-utils';
import { FusionManager } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-manager';
import { FusionMinion } from '../../../core/outputs/annotation/annotation-shared/fusion/fusion-minion';
import { MIN_MAX_MISSING_ERROR } from '../../../core/outputs/annotation/annotation-shared/fusion/projection/orthographic-orchestrator';
import { ShapeLayerState } from '../../../core/outputs/annotation/annotation-shared/state/layers/shape-layer-state';
import { AnnotationStateUndoService } from '../../../core/outputs/annotation/annotation-shared/state/root/annotation-state-undo.service';
import { ObjectlessToolbarKeyboardShortcutService } from '../../../core/outputs/annotation/annotation-shared/toolbars/static-toolbar/objectless-toolbar-keyboard-shortcut.service';
import { InteractionModeService } from '../../../core/outputs/annotation/annotation-shared/utils/interaction-mode.service';
import { KeyframeKeyboardShortcutsService } from '../../../core/outputs/annotation/annotation-shared/utils/keyframe-keyboard-shortcuts.service';
import { OutputKeyboardShortcutService } from '../../../core/outputs/annotation/annotation-shared/utils/output-keyboard-shortcut.service';
import { RootShapeSelectionService } from '../../../core/outputs/annotation/annotation-shared/utils/root-shape-selection.service';
import { SelectionService } from '../../../core/outputs/annotation/annotation-shared/utils/selection.service';
import { ShapeSelectionService } from '../../../core/outputs/annotation/annotation-shared/utils/shape-selection.service';
import {
  ZOOM_PANEL_WIDTH,
  ZoomPanelService,
} from '../../../core/outputs/annotation/annotation-shared/zoom-panel/zoom-panel.service';
import { DraftSubmissionService } from '../../../core/production-workspace/submissions/draft-submission.service';
import { SubmitTaskService } from '../../../core/production-workspace/submissions/submit-task.service';
import { SamplingWorkspaceStore } from '../../../core/sampling-workspace/state/sampling-workspace-store';
import { FeedbackListService } from '../../../core/shared/feedback/feedback-list.service';
import { AnnotationTransientInfoService } from '../../../core/shared/misc-services/annotation-transient-info.service';
import { FeatureFlagService } from '../../../core/shared/misc-services/feature-flag.service';
import { ObjectSortingService } from '../../../core/shared/misc-services/object-sorting.service';
import { ShapeVisibilityService } from '../../../core/shared/misc-services/shape-visibility.service';
import { AnnotationCopyService } from '../../../core/shared/shape-copy-and-paste/annotation-copy.service';
import { AnnotationPastingService } from '../../../core/shared/shape-copy-and-paste/annotation-pasting.service';
import { KeyboardShortcutName } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { UuidService } from '../../../cross-team-shared/misc-services/uuid.service';
import { selectionToArray } from '../../../cross-team-shared/misc-utils';
import { settingsStoreToken } from '../../../cross-team-shared/state/setting-state';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { subtractPoints } from '../../../shared/misc-utilities/point-utils';
import {
  ContentConnector,
  LoadTaskResult,
} from '../../../team-apis/content/content-connector';
import { ContentInitializedEvent } from '../../../team-apis/content/content-initialized-event';
import { ViewBox } from '../../../team-apis/content/view-box';
import { IN_PROGRESS_INDEX } from '../../../team-apis/core/reserved-shape-indexes';
import { Answer } from '../../../team-apis/otterdam/answers/answer';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { Task } from '../../../team-apis/otterdam/task';
import { AbstractOutputComponent } from '../../abstract-output-component';
import {
  feedbackDialogService,
  outputAnnotationInfoStoreToken,
} from './annotation-providers';
import { setSharedCssProperties } from './properties-shared-with-css';
import { AbstractAnnotationStore } from './state/root/abstract-annotation-store';
import { AnnotationState } from './state/root/annotation-state';
import { SensorRendererState } from './state/root/sensor-renderer-state';
import { TagService } from './utils/tag.service';

export const contentInjectorToken = new InjectionToken<Injector>(
  'injector with content services',
);

@Directive()
export abstract class AbstractAnnotationOutputComponent<
    StateType extends AnnotationState,
    OutputType extends AnnotationOutput,
    AnswerType extends Answer,
  >
  extends AbstractOutputComponent<OutputType, AnswerType>
  implements OnInit, OnDestroy
{
  featureFlagService = inject(FeatureFlagService);
  contentInjector?: Injector;
  contentConnector!: ContentConnector;

  protected draftSubmissionService = inject(DraftSubmissionService);
  protected eventTrackingService = inject(EventTrackingService);
  protected fusionManager = inject(FusionManager);
  protected fusionMinion!: FusionMinion;
  protected fusionStore = inject(fusionStoreToken);
  protected submitTaskService = inject(SubmitTaskService);
  protected shapesStore = inject(outputAnnotationInfoStoreToken)('shapes');
  protected objectlessToolbarKeyboardShortcutService = inject(
    ObjectlessToolbarKeyboardShortcutService,
  );

  protected isClientFeedbacksEditable: boolean;
  protected undoService = inject(AnnotationStateUndoService);
  protected isShapeFeedbackEditable: boolean;
  protected annotationOutputName =
    inject(outputInfoStoreToken).state().output.name;
  protected keyboardShortcutService = inject(OutputKeyboardShortcutService);
  protected sensorRendererStore = inject(outputAnnotationInfoStoreToken)(
    'sensorRendererState',
  );
  protected objectSortingService = inject(ObjectSortingService);
  protected rootShapeSelectionService = inject(RootShapeSelectionService);

  private tagService = inject(TagService);
  private shapeSelectionService = inject(ShapeSelectionService);
  private selectionService = inject(SelectionService);
  private annotationTransientInfoService = inject(
    AnnotationTransientInfoService,
  );
  private shapeVisibilityService = inject(ShapeVisibilityService);
  private feedbackDialogService = inject(feedbackDialogService);
  private feedbackListService = inject(FeedbackListService);
  private settingsStore = inject(settingsStoreToken);
  private annotationCopyService = inject(AnnotationCopyService);
  private annotationPastingService = inject(AnnotationPastingService);
  private interactionModeService = inject(InteractionModeService);
  private uuidService = inject(UuidService);
  private sampleStore = inject(SamplingWorkspaceStore);
  protected abstract keyboardShortcuts: KeyboardShortcutName[];

  constructor(
    protected abstractStore: AbstractAnnotationStore,
    injector: Injector,
  ) {
    super(injector);
    setSharedCssProperties(injector.get(ElementRef));
    this.isShapeFeedbackEditable = this.getIsShapeFeedbackEditable();
    this.isClientFeedbacksEditable = this.getIsClientFeedbackEditable();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setUpSaveCallbacks();
    if (this.taskWorkspaceStore.isUsingObjects()) {
      this.annotationCopyService.startListening();
      this.annotationPastingService.startListening();
    }
    this.shapeVisibilityService.startListening();
  }

  onContentInitialized(event: ContentInitializedEvent) {
    this.contentConnector = event.connector;
    this.contentInjector = event.injector;

    this.fusionMinion = new FusionMinion(
      this.outputInfoStore.state().output,
      this.abstractStore,
      event.connector,
      this.undoService,
      this.shapeSelectionService,
      this.selectionService,
      event.injector,
      this.feedbackListService,
      this.tagService,
      this.taskWorkspaceStore,
      this.sensorRendererStore,
      this.interactionModeService,
      this.injector.get(ZoomPanelService),
    );
    this.fusionManager.registerMinion(this.fusionMinion);
    this.setUpSubscriptions();
  }

  ngOnDestroy() {
    // Unsubscribe first so we don't have active subscriptions to a destroyed
    // store
    this.unsubscribe();
    this.fusionManager.unregisterMinion(this.fusionMinion);
  }

  protected setUpSubscriptions() {
    this.setUpTaskLoading();

    if (this.taskWorkspaceStore.isUsingObjects()) {
      this.setUpShapeTransientInfoSubscriptions();
    }

    this.subscribeToCompareAnswerChanged();
    this.subscribeToFrameNumber();
    this.setUpContentSync();
    this.subscribeToStatusUpdate();
    this.subscribeToEventTrackingModes();
    this.setUpKeyboardShortcutSubscriptions();
    this.setUpSubscriptionToPropagateViewBoxUpdate();
    this.setUpSelectedOutputSubscription();
    this.setUpSampleStatusSubscription();
    // since the keyframe service injects the content connector, we need to
    // inject this after content has initialized
    this.injector
      .get(KeyframeKeyboardShortcutsService)
      .registerKeyboardShortcuts();
  }

  protected buildSensorRendererState(result: LoadTaskResult) {
    const sensorFrames = times(result.frameCount, () => result.allFramesLoaded);
    return new SensorRendererState(sensorFrames, result.height, result.width);
  }

  protected getLayerState(): StateType['layers'] {
    return this.abstractStore.state().layers;
  }

  protected isBlank() {
    return this.contentConnector.isOutputBlank();
  }

  protected loadTask(task: Task) {
    return this.contentConnector.loadTask(task);
  }

  protected buildUpdateOutputInfoTrigger$(): Observable<unknown> {
    return this.fusionManager.undoService.state$;
  }

  protected cleanUpForNewTask() {
    this.setLoadingStatus();
    this.sensorRendererStore.delete();
  }

  protected resetRightPanelState() {
    const hasShapes = !isEmpty(
      // @ts-ignore -- fix me
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.annotationOutputName
      ].shapes,
    );
    // If any output in a sensor fusion task has shapes, expand the right panel
    if (!this.fusionStore.state().isRightPanelExpanded) {
      const isRightPanelExpanded = this.taskWorkspaceStore.isUsingObjects()
        ? true
        : hasShapes;
      this.fusionStore('isRightPanelExpanded').set(isRightPanelExpanded);
    }
  }

  protected updateShapeLayer() {}

  protected setUpTaskLoading() {
    // cleanUpForNewTask, loadTask and resetStoreForNewTask
    // can all be merged into a single load task method
    this.cleanUpForNewTask();
    const { task } = this.taskWorkspaceStore.state();
    this.subscribeTo(
      from(this.loadTask(task)),
      (loadTaskResult) => {
        this.resetStoreForNewTask(loadTaskResult);
        // viewbox, 3d camera and raster mask are set after content is loaded
        this.fusionManager.undoService.reset();
      },
      (error) => this.abstractStore.setError(error.message),
    );
  }

  protected setUpKeyboardShortcutSubscriptions() {
    for (const shortcut of this.keyboardShortcuts) {
      this.subscribeTo(
        this.keyboardShortcutService.getEvent$(shortcut),
        ({ keyboardEvent, eventTrackingValue }) => {
          this.contentConnector.handleKeyboardShortcut(
            shortcut,
            keyboardEvent,
            eventTrackingValue,
          );
        },
      );
    }
  }

  protected setSensorRendererState(result: LoadTaskResult) {
    this.sensorRendererStore.set(this.buildSensorRendererState(result));
  }

  protected setUpSubscriptionToPropagateViewBoxUpdate() {
    this.subscribeTo(this.taskWorkspaceStore('viewBoxes').$, (viewBoxes) => {
      forEach(viewBoxes, (viewBox, outputName) => {
        // @ts-ignore -- fix me
        this.contentConnector.handleViewBoxUpdate(outputName, viewBox);
      });
    });

    this.subscribeTo(this.taskWorkspaceStore('viewBoxPadding').$, (padding) => {
      this.contentConnector.handleViewBoxPaddingUpdate(padding);
    });

    this.subscribeTo(
      combineLatest([
        this.abstractStore('mousePosition').$.pipe(filter(isDefined)),
        this.abstractStore('zoomPanel')('scale').$.pipe(filter(isDefined)),
      ]),
      ([mousePosition, zoomPanelScale]) =>
        this.updateAndPropagateZoomPanelViewBox(mousePosition, zoomPanelScale),
    );
  }

  protected getShapeGroupOrderChange$(): Observable<unknown> {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return this.getShapeGroupOrderObjectless$(this.annotationOutputName);
    }
    return this.objectSortingService.getShapeOrder$(this.annotationOutputName);
  }

  protected getShapeGroupOrder() {
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return this.getShapeGroupOrderStoreObjectless(
        this.annotationOutputName,
      ).state();
    }
    return this.objectSortingService.getShapeOrder(this.annotationOutputName);
  }

  // eslint-disable-next-line complexity,max-params-unless-injectable -- out of ideas
  protected inspectFocusedShape(
    previousSelection: number[] | undefined,
    inspectMode: boolean,
    selectionChanged: boolean,
    frameNumberChanged: boolean,
    inspectModeChanged: boolean,
  ) {
    if (
      !isEqual([IN_PROGRESS_INDEX], previousSelection) &&
      inspectMode &&
      (selectionChanged || frameNumberChanged || inspectModeChanged)
    ) {
      void this.contentConnector.inspectFocusedShape();
    }
  }

  private getZoomPanelViewBox(
    mousePosition: Point2D,
    zoomPanelScale: number,
  ): ViewBox {
    const mainDisplaySize = this.abstractStore.state().mainDisplaySize;
    assert(
      mainDisplaySize,
      'mainDisplaySize is defined when the mouse position is defined',
    );
    const { width: mainDisplayWidth, height: mainDisplayHeight } =
      mainDisplaySize;

    // This ratio ensure that if the zoom panel scale is 1, the size of
    // the shapes in the zoom panel (in screen pixels) matches the ones
    // in the main display.
    const ratio =
      ZOOM_PANEL_WIDTH / Math.max(mainDisplayWidth, mainDisplayHeight);
    const zoomFactor = zoomPanelScale / ratio;
    const { width, height } = this.abstractStore.state().layers[0];
    const PADDING =
      zoomFactor === 0
        ? MIN_ZOOM_SIZE_MULTIPLIER
        : Math.min(width, height) / zoomFactor;

    const pan = subtractPoints(mousePosition, [PADDING, PADDING]);
    return [
      clamp(pan[0], -PADDING, width - PADDING),
      clamp(pan[1], -PADDING, height - PADDING),
      PADDING * 2,
      PADDING * 2,
    ];
  }

  private subscribeToCompareAnswerChanged() {
    this.subscribeTo(this.taskWorkspaceStore.compareAnswerChanged$, () => {
      this.updateShapeLayer();
      this.outputInfoStore('initialized').set(true);
    });
  }

  private subscribeToFrameNumber() {
    this.subscribeTo(
      this.fusionManager.getFrameNumberForOutput$(this.annotationOutputName),
      (frameNumber) => {
        this.abstractStore('frameNumber').set(frameNumber);
        this.contentConnector.setFrameNumber(frameNumber);
      },
    );
  }

  private subscribeToStatusUpdate() {
    this.subscribeTo(
      combineLatest([
        this.buildUpdateOutputInfoTrigger$(),
        this.abstractStore('status').$,
      ]).pipe(filter(([_, status]) => status !== 'loading')),
      this.updateOutputInfo,
    );
  }

  private setUpShapeTransientInfoSubscriptions() {
    // Keep this subscription separate, to trigger contentConnector only
    // when transient actually changes
    this.annotationTransientInfoService.setUpShapeTransientInfoContentSynchronization(
      this.contentConnector,
    );

    this.annotationTransientInfoService.syncContentScopedObjectId(
      this.contentConnector,
    );
  }

  private setUpSampleStatusSubscription() {
    this.subscribeTo(
      this.sampleStore.hasSampleFinalized$().pipe(
        distinctUntilChanged(),
        filter((hasSampleFinalized) => hasSampleFinalized),
      ),
      () => this.contentConnector.disableFeedbackCreation(),
    );
  }

  private setUpSaveCallbacks() {
    this.draftSubmissionService.addCallback(
      () => this.prepareForSaveAndSetAnswer(true),
      this,
    );
    this.submitTaskService.addCallback(
      () => this.prepareForSaveAndSetAnswer(false),
      this,
    );
  }

  private async prepareForSaveAndSetAnswer(isDraft: boolean) {
    const answer = await this.contentConnector.prepareForSave(isDraft);
    if (answer) {
      this.outputInfoStore('answer').set(answer);
      return true;
    }
    return false;
  }

  private setLoadingStatus() {
    this.abstractStore('status').set('loading');
    this.outputInfoStore.assign({
      unsubmittableReason: 'An output is still loading',
      canSaveDrafts: false,
    });
  }

  private updateOutputInfo() {
    const status = this.abstractStore.state().status;
    assert(status !== 'loading', "Can't call when status is loading");
    if (!this.taskWorkspaceStore.isUsingObjects()) {
      const layers = this.getLayerState();
      const answer =
        status === 'error' ? ({} as AnswerType) : this.answer(layers);
      this.outputInfoStore.assign({
        answer,
        unsubmittableReason: this.unsubmittableReason(),
        canSaveDrafts: status === 'ready',
        initialized: true,
      });
    } else {
      this.outputInfoStore.assign({
        unsubmittableReason: this.unsubmittableReason(),
        canSaveDrafts: status === 'ready',
      });
    }
  }

  // eslint-disable-next-line complexity -- it is reasonable to list all conditions here
  private unsubmittableReason() {
    if (
      this.abstractStore.state().status === 'error' &&
      this.abstractStore.state().errorMessage !== MIN_MAX_MISSING_ERROR
    ) {
      return 'An error occurred';
    }

    if (this.isBlank()) {
      if (this.outputInfoStore.state().output.required) {
        return 'A required output is blank';
      }
      return undefined;
    }

    if (
      !this.taskWorkspaceStore.isUsingObjects() &&
      this.hasMissingRequiredTagObjectless()
    ) {
      return 'A shape is missing a required tag';
    }
    return undefined;
  }

  /** @deprecated used for old projects without objects */
  private hasMissingRequiredTagObjectless() {
    return some(this.shapesStore.state(), (shape) =>
      // @ts-ignore -- fix me
      this.tagService.isShapeMissingRequiredTag(shape),
    );
  }

  private getIsShapeFeedbackEditable() {
    return (
      this.taskWorkspaceStore.state().feedbackType === 'shape' &&
      this.feedbackDialogService.isEditable()
    );
  }

  private getIsClientFeedbackEditable() {
    return (
      this.taskWorkspaceStore.state().feedbackType === 'client' &&
      this.feedbackDialogService.isEditable()
    );
  }

  private subscribeToEventTrackingModes() {
    this.subscribeTo(
      combineLatest([
        this.settingsStore('inspectMode').$.pipe(
          map((inspectMode) => inspectMode && 'inspect'),
        ),
        this.abstractStore('zoomPanel')('isOpen').$.pipe(
          map((isZoomPanelOpen) => isZoomPanelOpen && 'zoom_panel'),
        ),
      ]).pipe(
        map(([inspectEventContext, zoomPanelEventContext]) => {
          if (inspectEventContext && zoomPanelEventContext) {
            return 'inspect+zoom_panel';
          }
          return (
            inspectEventContext || zoomPanelEventContext || 'free_navigation'
          );
        }),
      ),
      (mode) => this.eventTrackingService.addContext({ mode }),
    );
  }

  // eslint-disable-next-line max-lines-per-function -- long but not too complicated
  private setUpContentSync() {
    this.subscribeTo(
      combineLatest([
        this.getShapeGroupOrderChange$().pipe(
          map(() => this.uuidService.getUuidV4()),
        ),
        this.getSelection$(),
        this.fusionManager.fusionStore('frameNumber').$,
        this.settingsStore('inspectMode').$,
      ]).pipe(withHistory(1)),
      ([
        [shapeGroupOrder, selection, frameNumber, inspectMode],
        [
          previousShapeGroupOrder,
          previousSelection,
          previousFrameNumber,
          previousInspectMode,
        ] = [],
      ]) => {
        const selectionChanged = selection !== previousSelection;

        if (shapeGroupOrder !== previousShapeGroupOrder || selectionChanged) {
          this.contentConnector.setShapes(this.getShapeGroupOrder());
          this.contentConnector.setSelection(this.getSelection());
        }

        const frameNumberChanged = frameNumber !== previousFrameNumber;
        const inspectModeChanged = inspectMode !== previousInspectMode;
        this.inspectFocusedShape(
          previousSelection,
          inspectMode,
          selectionChanged,
          frameNumberChanged,
          inspectModeChanged,
        );
      },
    );
  }

  private getSelection$() {
    const selection$ = this.rootShapeSelectionService.getSelection$(true);
    return selection$.pipe(
      map(selectionToArray),
      distinctUntilChanged(isEqual),
    );
  }

  private getSelection() {
    const selection = this.rootShapeSelectionService.getNestedSelection(true);
    return selectionToArray(selection);
  }

  private updateAndPropagateZoomPanelViewBox(
    mousePosition: {
      imagePoint: Point2D;
    },
    zoomPanelScale: number,
  ) {
    const viewBox = this.getZoomPanelViewBox(
      mousePosition.imagePoint,
      zoomPanelScale,
    );
    const output = this.outputInfoStore.state().output;
    this.contentConnector.handleZoomPanelViewBoxUpdate(
      this.annotationOutputName,
      viewBox,
    );
    if (this.fusionManager.output.id !== output.id) {
      this.fusionManager
        .getPrimaryMinion()
        .contentConnector.handleZoomPanelViewBoxUpdate(
          this.annotationOutputName,
          viewBox,
        );
    }
  }

  private setUpSelectedOutputSubscription() {
    this.subscribeTo(
      this.taskWorkspaceStore('selectedOutputName').$.pipe(
        map(
          (selectedOutputName) =>
            selectedOutputName === this.annotationOutputName,
        ),
        distinctUntilChanged(),
      ),
      (isSelected) =>
        isSelected
          ? this.contentConnector.activateSelectedTool()
          : this.contentConnector.deactivateSelectedTool(),
    );
  }

  private getShapeGroupOrderStoreObjectless(outputName: string) {
    return getOutputAnnotationInfoStore(this.taskWorkspaceStore, outputName);
  }

  private getShapeGroupOrderObjectless$(outputName: string) {
    return this.getShapeGroupOrderStoreObjectless(outputName).$.pipe(
      // ignore changes to sensorRendererState
      distinctUntilChanged(
        (a, b) =>
          a.shapes === b.shapes &&
          a.groups === b.groups &&
          a.shapeAndGroupOrder === b.shapeAndGroupOrder,
      ),
    );
  }

  protected abstract answer(layers: StateType['layers']): AnswerType;

  /**
   * This should do no asynchronous work. If it does another task may load
   * during that time, and updating the store after that with info from this
   * asset would be a bug.
   */
  protected abstract resetStoreForNewTask(result: LoadTaskResult): void;

  protected abstract createLayerState(
    width: number,
    height: number,
  ): ShapeLayerState;
}
